import { formatApiTimeLocal } from '@/utils/date/date-time'

export interface IRealtimeHistoricalDataPoint {
  time: string
  timestampMs: number
  [key: string]: number | null | string | undefined
}

export interface IRealtimeChartSeriesConfig {
  name: string
  key: string
  color: string
}

export type EnergyConsoleMetricGroup = 'current' | 'voltage' | 'powerFactor'

type IEnergyReadingPoint = { t?: string; v?: number | string; time?: string; value?: number | string }

const CURRENT_KEY_ORDER = ['current_a', 'current_b', 'current_c', 'average_current'] as const
const VOLTAGE_KEY_ORDER = ['voltage_a', 'voltage_b', 'voltage_c'] as const
const POWER_FACTOR_KEY_ORDER = ['power_factor', 'power_factor_a', 'power_factor_b', 'power_factor_c'] as const

// --- classificação de chaves do console ---

export function isCurrentFieldKey(key: string): boolean {
  return key === 'average_current' || key.startsWith('current_')
}

export function isVoltageFieldKey(key: string): boolean {
  return key.startsWith('voltage_')
}

export function isPowerFactorFieldKey(key: string): boolean {
  return key === 'power_factor' || key.startsWith('power_factor_')
}

function metricGroupOf(key: string): EnergyConsoleMetricGroup | null {
  if (isCurrentFieldKey(key)) return 'current'
  if (isVoltageFieldKey(key)) return 'voltage'
  if (isPowerFactorFieldKey(key)) return 'powerFactor'
  return null
}

function canonicalOrderFor(group: EnergyConsoleMetricGroup): readonly string[] {
  if (group === 'voltage') return VOLTAGE_KEY_ORDER
  if (group === 'powerFactor') return POWER_FACTOR_KEY_ORDER
  return CURRENT_KEY_ORDER
}

function sortKeysForGroup(keys: Iterable<string>, group: EnergyConsoleMetricGroup): string[] {
  const keySet = new Set(keys)
  const ordered = canonicalOrderFor(group).filter((key) => keySet.has(key))
  const orderedSet = new Set(ordered)
  return [...ordered, ...Array.from(keySet).filter((key) => !orderedSet.has(key))]
}

// --- parsing / schema ---

export function parseReadingNumericValue(value: unknown): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

/** Schema canônico → chave da API; mapa inverso para normalizar readings. */
function buildFieldKeyMap(schema: Record<string, string> | null | undefined): Record<string, string> {
  const map: Record<string, string> = {}
  if (!schema || typeof schema !== 'object') return map

  for (const [canonicalKey, apiKey] of Object.entries(schema)) {
    const trimmedApiKey = String(apiKey ?? '').trim()
    if (!trimmedApiKey) continue
    map[trimmedApiKey] = canonicalKey
    map[canonicalKey] = canonicalKey
  }
  return map
}

function resolveCanonicalFieldKey(rawKey: string, fieldKeyMap: Record<string, string>): string {
  if (fieldKeyMap[rawKey]) return fieldKeyMap[rawKey]
  const lower = rawKey.toLowerCase()
  const matched = Object.entries(fieldKeyMap).find(([key]) => key.toLowerCase() === lower)
  return matched?.[1] ?? rawKey
}

// --- histórico (seed + polling) ---

export function buildHistoricalDataPointsFromEnergyReadings(
  readings: Record<string, Array<IEnergyReadingPoint>> | null | undefined,
  schema?: Record<string, string> | null,
): IRealtimeHistoricalDataPoint[] {
  if (!readings || typeof readings !== 'object') return []

  const fieldKeyMap = buildFieldKeyMap(schema)
  const byTime: Record<string, IRealtimeHistoricalDataPoint> = {}

  for (const [rawField, points] of Object.entries(readings)) {
    if (!Array.isArray(points)) continue
    const field = resolveCanonicalFieldKey(rawField, fieldKeyMap)

    for (const point of points) {
      const t = point.t ?? point.time
      if (t === undefined || t === null || t === '') continue

      const rawValue = point.v ?? point.value
      const v = parseReadingNumericValue(rawValue)
      if (v === null) continue

      const iso = String(t)
      const timestampMs = new Date(iso).getTime()
      if (Number.isNaN(timestampMs)) continue

      if (!byTime[iso]) {
        byTime[iso] = {
          time: formatApiTimeLocal(iso),
          timestampMs,
        }
      }
      byTime[iso][field] = v
    }
  }

  return Object.keys(byTime)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map((iso) => byTime[iso])
}

/** Une pontos históricos por timestampMs (ex.: corrente + tensão/FP). */
export function mergeHistoricalDataPoints(
  primary: IRealtimeHistoricalDataPoint[],
  secondary: IRealtimeHistoricalDataPoint[],
): IRealtimeHistoricalDataPoint[] {
  const byTimestamp = new Map<number, IRealtimeHistoricalDataPoint>()

  for (const point of [...primary, ...secondary]) {
    if (typeof point.timestampMs !== 'number' || Number.isNaN(point.timestampMs)) continue
    const existing = byTimestamp.get(point.timestampMs)
    byTimestamp.set(point.timestampMs, existing ? { ...existing, ...point } : { ...point })
  }

  return Array.from(byTimestamp.values()).sort((a, b) => a.timestampMs - b.timestampMs)
}

export function normalizeInstantReadingsForHistory(
  readings: Record<string, unknown>,
  schema: Record<string, string> | null | undefined,
): Record<string, number | null> {
  const fieldKeyMap = buildFieldKeyMap(schema)
  const normalized: Record<string, number | null> = {}

  for (const [rawKey, rawValue] of Object.entries(readings)) {
    normalized[resolveCanonicalFieldKey(rawKey, fieldKeyMap)] = parseReadingNumericValue(rawValue)
  }
  return normalized
}

export function getLastHistoricalTimestampMs(points: IRealtimeHistoricalDataPoint[]): number | null {
  if (!points.length) return null
  const last = points[points.length - 1]
  return typeof last.timestampMs === 'number' && !Number.isNaN(last.timestampMs) ? last.timestampMs : null
}

export function extractDynamicReadingKeys(
  readings: Record<string, unknown> | null | undefined,
): string[] {
  if (!readings || typeof readings !== 'object') return []
  return Object.entries(readings)
    .filter(([, points]) => Array.isArray(points) && points.length > 0)
    .map(([key]) => key)
}

export function normalizeFixedValues(
  fixedValues: Record<string, unknown> | null | undefined,
): Record<string, number> {
  const normalized: Record<string, number> = {}
  if (!fixedValues || typeof fixedValues !== 'object') return normalized

  for (const [key, value] of Object.entries(fixedValues)) {
    const parsed = parseReadingNumericValue(value)
    if (parsed !== null) normalized[key] = parsed
  }
  return normalized
}

// --- console: séries, grupos, fixed_values ---

export function filterSeriesByAvailableKeys(
  series: IRealtimeChartSeriesConfig[],
  availableKeys: Iterable<string>,
): IRealtimeChartSeriesConfig[] {
  const keySet = new Set(availableKeys)
  return series.filter((item) => keySet.has(item.key))
}

/** readings dinâmicos ∪ fixed_values. */
export function collectAvailableConsoleMetricKeys(
  dynamicKeys: Iterable<string>,
  fixedValues: Record<string, number> | null | undefined,
): string[] {
  const keys = new Set<string>()
  for (const key of dynamicKeys) {
    if (key) keys.add(key)
  }
  if (fixedValues) {
    for (const [key, value] of Object.entries(fixedValues)) {
      if (typeof value === 'number' && !Number.isNaN(value)) keys.add(key)
    }
  }
  return Array.from(keys)
}

export function filterKeysByMetricGroup(
  keys: Iterable<string>,
  group: EnergyConsoleMetricGroup,
): string[] {
  return sortKeysForGroup(
    Array.from(keys).filter((key) => metricGroupOf(key) === group),
    group,
  )
}

export function shouldShowEnergyConsoleMetricGroup(
  group: EnergyConsoleMetricGroup,
  dynamicKeys: Iterable<string>,
  fixedValues: Record<string, number> | null | undefined,
): boolean {
  return filterKeysByMetricGroup(collectAvailableConsoleMetricKeys(dynamicKeys, fixedValues), group).length > 0
}

export function splitFixedValuesByGroup(fixedValues: Record<string, number> | null | undefined): {
  voltage: Record<string, number>
  powerFactor: Record<string, number>
} {
  const voltage: Record<string, number> = {}
  const powerFactor: Record<string, number> = {}
  if (!fixedValues) return { voltage, powerFactor }

  for (const [key, value] of Object.entries(fixedValues)) {
    if (typeof value !== 'number' || Number.isNaN(value)) continue
    if (isVoltageFieldKey(key)) voltage[key] = value
    else if (isPowerFactorFieldKey(key)) powerFactor[key] = value
  }
  return { voltage, powerFactor }
}

export function isFixedValueKey(
  key: string,
  fixedValues: Record<string, number> | null | undefined,
): boolean {
  return Boolean(fixedValues && Object.prototype.hasOwnProperty.call(fixedValues, key))
}

export function pickRecordEntriesByKeys<T>(
  record: Record<string, T>,
  allowedKeys: Iterable<string>,
): Record<string, T> {
  const result: Record<string, T> = {}
  for (const key of allowedKeys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      result[key] = record[key]
    }
  }
  return result
}
